// 일일 콘텐츠 지갑 지급(L9, src/shared/daily.ts Payout 계약): 일일 장부의 미지급 건을 고정 id로 지갑에 넣고, 두 장부 저장이 확인되면 지급 표시를 한다.
// 골드 = save.v1 보너스 영수증(id = payout.id, 지워지지 않는 영수증이라 같은 id는 영원히 한 번) · 강화석·보호 주문서 = 장비 장부 고정 id 연산(dl: 접두, 오래 기억).
// 레이드 정산(flow.settleRaids)과 같은 모양: 저장 확인 전에는 몇 번을 다시 넣어도(두 탭·새로고침·재접속) 받는 쪽이 같은 id를 걸러 정확히 한 번 반영된다.
import { unpaid, type Payout } from '../shared/daily';
import type { RegionId } from '../shared/levels';
import type { DailyStore } from './daily';
import type { GearStore } from './gear';
import type { ProfileStore } from './profile';
import type { SaveStore } from './save';

export interface DailyPayGame {
  readonly daily: DailyStore;
  readonly save: Pick<SaveStore, 'getResult' | 'hasResult' | 'recordResult' | 'savePending'>;
  readonly gear: Pick<GearStore, 'submit' | 'flush'>;
  readonly profile: Pick<ProfileStore, 'sync'>;
}

/** 보너스 영수증 지역(save.v1은 r1~r4만 안다 — 지역 표시용이고 골드 값은 이미 지역 배율이 곱해져 있다) */
const regionOf = (town: number): RegionId => ('r' + Math.max(1, Math.min(4, town))) as RegionId;

function submit(g: DailyPayGame, p: Payout): void {
  if (p.gold > 0 && !g.save.hasResult(p.id)) {
    g.save.recordResult({ id: p.id, region: regionOf(p.town), baseGold: p.gold, clearTicks: 0, maxCombo: 0, stars: 0, starMask: 0, kills: 0, damageDealt: 0, cards: [0, 0, 0], pickedCard: 0, bonus: true });
  }
  if (p.stones > 0) g.gear.submit({ id: p.id + ':s', t: 'stones', tier: p.tier, n: p.stones });
  if (p.protect > 0) g.gear.submit({ id: p.id + ':p', t: 'protect', n: p.protect });
}

/**
 * 미지급 건 정산. refresh = 저장소를 다시 읽어 다른 탭이 지급한 표시·새 건을 합친다(일반 틱은 false: 저장소를 읽지 않는다).
 * 돌려주는 값 = 이번에 지급 표시까지 끝난 건 수.
 */
export function settleDaily(g: DailyPayGame, refresh = false): number {
  // 정산 주기(1초)마다 불린다: 복제 없는 조회로 먼저 본다. 지급 완료 표시를 못 쓴 채(unsaved) 남았으면 미지급이 없어도 다시 읽고 쓴다(PR #273 Codex 5차)
  let list = unpaid(g.daily.peek);
  if (list.length === 0 && !refresh && !g.daily.unsaved) return 0;
  list = unpaid(g.daily.refresh());
  if (list.length === 0) return 0;
  for (const p of list) submit(g, p);
  const gearOk = g.gear.flush(true);
  const saveOk = !g.save.savePending;
  let done = 0;
  for (const p of list) {
    const goldOk = p.gold === 0 || (saveOk && g.save.hasResult(p.id));
    const itemOk = (p.stones === 0 && p.protect === 0) || gearOk;
    if (goldOk && itemOk && g.daily.markPaid(p.id)) done++;
  }
  g.profile.sync();
  return done;
}
